<template>
  <div v-if="isVisible" class="auth-modal-overlay" @click="onOverlayClick">
    <div class="auth-modal" @click.stop>
      <div class="auth-modal__header">
        <h2>Admin Authentication Required</h2>
        <p>Enter the admin password to access admin features.</p>
      </div>
      
      <form @submit.prevent="onSubmit" class="auth-modal__form">
        <div class="form-group">
          <label for="admin-password" class="form-label">
            Admin Password
          </label>
          <input
            id="admin-password"
            ref="passwordInput"
            v-model="password"
            type="password"
            class="form-input"
            :class="{ 'form-input--error': hasError }"
            placeholder="Enter admin password"
            autocomplete="current-password"
            :disabled="isLoading"
            @input="clearError"
          >
          <div v-if="hasError" class="form-error">
            {{ errorMessage }}
          </div>
        </div>
        
        <div class="auth-modal__actions">
          <button
            v-if="allowCancel"
            type="button"
            class="btn btn--secondary"
            @click="onCancel"
            :disabled="isLoading"
          >
            Cancel
          </button>
          <button
            type="submit"
            class="btn btn--primary"
            :disabled="isLoading || !password.trim()"
          >
            <span v-if="isLoading" class="btn-loading">
              <span class="spinner"></span>
              Authenticating...
            </span>
            <span v-else>Authenticate</span>
          </button>
        </div>
      </form>
      
      <div class="auth-modal__info">
        <p class="text-sm text-muted">
          <strong>Privacy Notice:</strong> Your authentication will be remembered for 8 hours 
          in this browser session. No passwords are stored permanently.
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, nextTick, onMounted, onUnmounted, watch } from 'vue'
import { useAuthStore } from '@/stores/auth'

export default {
  name: 'AdminAuthModal',
  emits: ['authenticated', 'cancelled', 'close'],
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    autoFocus: {
      type: Boolean,
      default: true
    },
    allowCancel: {
      type: Boolean,
      default: true
    }
  },
  setup(props, { emit }) {
    const authStore = useAuthStore()
    
    // Local state
    const password = ref('')
    const hasError = ref(false)
    const errorMessage = ref('')
    const passwordInput = ref(null)

    // Computed
    const isVisible = computed(() => props.visible)
    const isLoading = computed(() => authStore.isLoading)

    // Methods
    const clearError = () => {
      hasError.value = false
      errorMessage.value = ''
    }

    const focusInput = async () => {
      if (props.autoFocus && passwordInput.value) {
        await nextTick()
        passwordInput.value.focus()
      }
    }

    const onSubmit = async () => {
      clearError()
      
      if (!password.value.trim()) {
        hasError.value = true
        errorMessage.value = 'Password is required'
        return
      }

      try {
        await authStore.validatePassword(password.value.trim())
        
        // Success - emit authenticated event and clear form
        password.value = ''
        emit('authenticated')
        emit('close')
      } catch (error) {
        hasError.value = true
        errorMessage.value = error.message || 'Authentication failed'
        
        // Refocus input for retry
        await nextTick()
        if (passwordInput.value) {
          passwordInput.value.focus()
          passwordInput.value.select()
        }
      }
    }

    const onCancel = () => {
      if (!props.allowCancel) return
      
      password.value = ''
      clearError()
      emit('cancelled')
      emit('close')
    }

    const onOverlayClick = () => {
      if (props.allowCancel) {
        onCancel()
      }
    }

    const handleEscapeKey = (event) => {
      if (event.key === 'Escape' && props.allowCancel && isVisible.value) {
        onCancel()
      }
    }

    // Lifecycle
    onMounted(() => {
      document.addEventListener('keydown', handleEscapeKey)
      if (isVisible.value) {
        focusInput()
      }
    })

    onUnmounted(() => {
      document.removeEventListener('keydown', handleEscapeKey)
    })

    // Watch for visibility changes to focus input
    watch(isVisible, (newVisible) => {
      if (newVisible) {
        nextTick(focusInput)
      }
    })

    return {
      // State
      password,
      hasError,
      errorMessage,
      passwordInput,
      
      // Computed
      isVisible,
      isLoading,
      
      // Methods
      onSubmit,
      onCancel,
      onOverlayClick,
      clearError
    }
  }
}
</script>

<style scoped>
.auth-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

.auth-modal {
  background: #1a1a1a;
  border: 1px solid #333;
  border-radius: 12px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.5), 0 10px 10px -5px rgba(0, 0, 0, 0.3);
  max-width: 400px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
}

.auth-modal__header {
  padding: 1.5rem 1.5rem 1rem;
  border-bottom: 1px solid #333;
}

.auth-modal__header h2 {
  margin: 0 0 0.5rem;
  font-size: 1.25rem;
  font-weight: 600;
  color: #ffffff;
}

.auth-modal__header p {
  margin: 0;
  color: #9ca3af;
  font-size: 0.875rem;
  line-height: 1.4;
}

.auth-modal__form {
  padding: 1.5rem;
}

.form-group {
  margin-bottom: 1rem;
}

.form-label {
  display: block;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: #d1d5db;
}

.form-input {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #444;
  border-radius: 6px;
  font-size: 1rem;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  background: #2a2a2a;
  color: #ffffff;
}

.form-input[type="password"] {
  font-family: Verdana, sans-serif;
  letter-spacing: 0.125em;
}

.form-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
}

.form-input--error {
  border-color: #ef4444;
}

.form-input--error:focus {
  border-color: #ef4444;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.15);
}

.form-input:disabled {
  background-color: #222;
  opacity: 0.6;
  cursor: not-allowed;
}

.form-error {
  margin-top: 0.5rem;
  font-size: 0.875rem;
  color: #f87171;
}

.auth-modal__actions {
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  margin-top: 1.5rem;
}

.btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease-in-out;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 2.5rem;
}

.btn:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.btn--secondary {
  background: #2a2a2a;
  color: #d1d5db;
  border: 1px solid #444;
}

.btn--secondary:hover:not(:disabled) {
  background: #333;
  border-color: #555;
}

.btn--primary {
  background: #3b82f6;
  color: white;
}

.btn--primary:hover:not(:disabled) {
  background: #2563eb;
}

.btn-loading {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.spinner {
  width: 1rem;
  height: 1rem;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.auth-modal__info {
  padding: 1rem 1.5rem 1.5rem;
  border-top: 1px solid #333;
  background: #151515;
}

.text-sm {
  font-size: 0.875rem;
}

.text-muted {
  color: #9ca3af;
  line-height: 1.4;
}

/* Responsive adjustments */
@media (max-width: 640px) {
  .auth-modal {
    margin: 1rem;
    max-width: none;
  }
  
  .auth-modal__actions {
    flex-direction: column-reverse;
  }
  
  .btn {
    width: 100%;
  }
}
</style>